import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Logo from "../Images/Logo.png";

function scrollBehavior() {
  return window.matchMedia("(pointer: coarse)").matches ? "auto" : "smooth";
}

function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [darkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const ids = [
      "home",
      "AboutUs",
      "WhatWeBuilt",
      "services",
      "OurApproach",
      "WhyChooseUs",
      "ProposedSolution",
      "ContactUs",
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        let topMost = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (topMost && topMost.target && topMost.target.id) {
          setActiveSection(topMost.target.id);
        }
      },
      {
        root: null,
        rootMargin: "-30% 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: darkMode ? "#90caf9" : "#1976d2",
      },
      background: {
        default: darkMode ? "#121212" : "#ffffff",
        paper: darkMode ? "#1e1e1e" : "#f5f5f5",
      },
      text: {
        primary: darkMode ? "#ffffff" : "#000000",
      },
    },
    direction: i18n.dir(i18n.language),
  });

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [open, setOpen] = useState(false);

  const toggleDrawer = (state) => () => {
    setOpen(state);
  };

  const handleNavigation = (path) => {
    setOpen(false);

    const scrollToTarget = () => {
      if (path === "#home") {
        window.scrollTo({ top: 0, behavior: scrollBehavior() });
        return true;
      }
      const element = document.getElementById(path.substring(1));
      if (!element) return false;
      element.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
      return true;
    };

    if (location.pathname !== "/") {
      navigate("/");
    }

    if (scrollToTarget()) return;

    let attempts = 0;
    const waitForSection = () => {
      if (scrollToTarget() || attempts > 30) return;
      attempts += 1;
      requestAnimationFrame(waitForSection);
    };
    requestAnimationFrame(waitForSection);
  };

  const navLinks = [
    { text: t("About Us"), path: "#AboutUs" },
    { text: t("What We Have Built"), path: "#WhatWeBuilt" },
    { text: t("Services"), path: "#services" },
    { text: t("Our Approach"), path: "#OurApproach" },
    { text: t("Why Choose Us"), path: "#WhyChooseUs" },
    { text: t("Proposed Solution"), path: "#ProposedSolution" },
    { text: t("Contact Us"), path: "#ContactUs" },
  ];
  const pageLinks = navLinks.slice(0, -1);
  const contactLink = navLinks[navLinks.length - 1];

  const linkButtonSx = (isActive) => ({
    color: isActive ? "#ffffff" : "rgba(255,255,255,0.78)",
    cursor: "pointer",
    fontSize: "0.92rem",
    fontWeight: 600,
    textTransform: "none",
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    borderRadius: "999px",
    backgroundColor: isActive ? "rgba(0, 212, 255, 0.16)" : "transparent",
    transition: "background-color 0.25s ease, color 0.25s ease",
    "&:hover": {
      color: "#ffffff",
      backgroundColor: isActive
        ? "rgba(0, 212, 255, 0.24)"
        : "rgba(255,255,255,0.08)",
    },
  });

  const brand = (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      <Box
        component="a"
        href="#/"
        onClick={(e) => {
          e.preventDefault();
          handleNavigation("#home");
        }}
        sx={{
          display: "flex",
          lineHeight: 0,
          borderRadius: "50%",
          transition: "transform 0.25s ease",
          "&:hover": { transform: "scale(1.06)" },
        }}
      >
        <img
          src={Logo}
          alt={t("TechTopia Logo")}
          width={42}
          height={42}
          style={{ borderRadius: "50%", display: "block" }}
        />
      </Box>
      <Typography
        variant="h6"
        sx={{
          ml: 1.25,
          color: "#ffffff",
          fontSize: isMobile ? "1.15rem" : "1.25rem",
          fontWeight: 750,
          letterSpacing: "-0.3px",
          whiteSpace: "nowrap",
        }}
      >
        {t("TechTopia")}
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          top: { xs: 10, md: 16 },
          left: { xs: 10, lg: "50%" },
          right: { xs: 10, lg: "auto" },
          transform: { xs: "none", lg: "translateX(-50%)" },
          width: { xs: "auto", lg: "min(1320px, calc(100% - 40px))" },
          borderRadius: "18px",
          backgroundColor: scrolled
            ? "rgba(6, 12, 24, 0.96)"
            : "rgba(6, 12, 24, 0.92)",
          backdropFilter: { xs: "none", lg: "blur(18px)" },
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: scrolled
            ? "0 16px 40px rgba(0,0,0,0.4)"
            : "0 10px 28px rgba(0,0,0,0.22)",
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: 64, md: 68 },
            px: { xs: 1.5, md: 2 },
            gap: 2,
          }}
        >
          {brand}

          {!isMobile ? (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              {pageLinks.map(({ text, path }) => {
                const isActive = activeSection === path.substring(1);
                return (
                  <Button
                    key={text}
                    onClick={() => handleNavigation(path)}
                    sx={linkButtonSx(isActive)}
                  >
                    {text}
                  </Button>
                );
              })}
              <Button
                onClick={() => handleNavigation(contactLink.path)}
                sx={{
                  ml: 1,
                  px: 2.25,
                  py: 0.85,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#041018",
                  background:
                    "linear-gradient(90deg, #7ee8ff 0%, #42a5f5 100%)",
                  boxShadow: "0 6px 16px rgba(66,165,245,0.35)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #b6f3ff 0%, #64b5f6 100%)",
                  },
                }}
              >
                {contactLink.text}
              </Button>
            </Box>
          ) : (
            <>
              <IconButton
                onClick={toggleDrawer(true)}
                aria-label="Open menu"
                sx={{
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.16)",
                  borderRadius: "12px",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor={theme.direction === "rtl" ? "left" : "right"}
                open={open}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    width: "min(86vw, 360px)",
                    bgcolor: "#07101c",
                    color: "#ffffff",
                    borderLeft: "1px solid rgba(255,255,255,0.08)",
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    {brand}
                    <IconButton
                      onClick={toggleDrawer(false)}
                      aria-label="Close menu"
                      sx={{ color: "#ffffff" }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1 }} />
                  <List sx={{ flexGrow: 1 }}>
                    {navLinks.map(({ text, path }) => {
                      const isActive = activeSection === path.substring(1);
                      const isContact = path === contactLink.path;
                      return (
                        <ListItem key={text} disablePadding>
                          <ListItemButton
                            onClick={() => handleNavigation(path)}
                            sx={{
                              borderRadius: "12px",
                              my: 0.6,
                              py: 1.25,
                              backgroundColor: isContact
                                ? "transparent"
                                : isActive
                                ? "rgba(0, 212, 255, 0.16)"
                                : "transparent",
                              background: isContact
                                ? "linear-gradient(90deg, #7ee8ff 0%, #42a5f5 100%)"
                                : undefined,
                              "&:hover": {
                                backgroundColor: isContact
                                  ? undefined
                                  : "rgba(255,255,255,0.08)",
                              },
                            }}
                          >
                            <ListItemText
                              primary={text}
                              primaryTypographyProps={{
                                sx: {
                                  fontWeight: 650,
                                  fontSize: "1.05rem",
                                  color: isContact
                                    ? "#041018"
                                    : isActive
                                    ? "#ffffff"
                                    : "rgba(255,255,255,0.86)",
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Drawer>
            </>
          )}
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
}

export default Navbar;
